import {GetInitialMonthlyMinutesUsed} from '../office-js/HostAppSettings';
import {
  AVAILABLE_UPLOAD_MINUTES_PER_MONTH,
  IS_DEV_BUILD,
  ROAMING_SETTING_MONTHLY_QUOTA_KEY,
  MAX_MINUTES_GET_TIME_MILLISECONDS,
  NEW_AVAILABLE_UPLOAD_MINUTES_PER_MONTH,
} from '../../AppConstants';
import {LogEvents} from '../../utils/LogEvents';
import {MonthlyQuotaKeys} from '../../backend/telemetry/MonthlyQuotaKeys';
import {LogEventNames} from '../../backend/interfaces/ITelemetryLogger';
import {LogError} from '../../utils/ErrorTelemetry';
import {ErrorStringFromException, PromiseWithTimeout, IsUnlimitedFileUploadMinutesAvailableForUser, IsCopilotLicensedUser} from '../../utils/utils';

// This class uses host app messaging to send messages to the host (WAC).
// WAC then handles our messages by making requests to its RoamingServiceHandler, which
// reads and writes values from the roaming service.
// Some pointers to where the code lives:
// Host message handling: wac/src/WordEditor/Client/Packages/Transcription/TranscriptionPaneLoader/SDXAddInLoader/TranscriptionAddInMessaging.cs
// Host quota handling: wac/src/WordEditor/Client/Packages/Transcription/TranscriptionPaneLoader/SDXAddInLoader/TranscriptionMonthlyQuotaHandler.cs
// TODO: Add location of RoamingServiceHandler handlers once they are checked in.
// Roaming service setting definitions (our setting names are ridTranscriptionQuotaMinutesUsed and ridTranscriptionQuotaLastResetTimestamp):
// https://office.visualstudio.com/ISS/_git/Service-Utilities?path=%2Fsrc%2FTransformer%2FRoamingDefinitions%2FRoamingDefinitions.xml
export class TranscriptionMonthlyQuota {
  private usedMinutesCache: number = 0;

  private static instance: TranscriptionMonthlyQuota | null = null;

  static GetInstance() {
    if (this.instance == null) this.instance = new TranscriptionMonthlyQuota();

    return this.instance;
  }

  constructor() {
    LogEvents.MonthlyQuota.Start();
    this.usedMinutesCache = GetInitialMonthlyMinutesUsed();
    LogEvents.MonthlyQuota.End([
      {key: MonthlyQuotaKeys.InitialMinutesUsed, value: this.usedMinutesCache},
    ]);
  }

  public GetCachedUsedMinutes(): number {
    return this.usedMinutesCache;
  }

  public GetCachedPercentTimeUsed(): number {
    if (IsUnlimitedFileUploadMinutesAvailableForUser() || IsCopilotLicensedUser()) {
      /* On uncapping minutes limit, we will be keeping an internal very
       high limit of 30000 minutes to avoid feature misuse.*/
      return this.usedMinutesCache / NEW_AVAILABLE_UPLOAD_MINUTES_PER_MONTH;
    }

    return this.usedMinutesCache / AVAILABLE_UPLOAD_MINUTES_PER_MONTH;
  }

  public GetAvailableUploadMinutesPerMonth(): number {
    if (IsUnlimitedFileUploadMinutesAvailableForUser() || IsCopilotLicensedUser()) {
      /* On uncapping minutes limit, we will be keeping an internal very
       high limit of 30000 minutes to avoid feature misuse.*/
      return NEW_AVAILABLE_UPLOAD_MINUTES_PER_MONTH;
    }

    return AVAILABLE_UPLOAD_MINUTES_PER_MONTH;
  }

  public async AddMinutesUsed(numMinutesUsed: number): Promise<void> {
    if (IS_DEV_BUILD) return;

    LogEvents.MonthlyQuota.Start();
    await OfficeCore.run((context) => {
      let setting = context.firstParty.roamingSettings
        .getItem(ROAMING_SETTING_MONTHLY_QUOTA_KEY)
        .load('value');
      return context.sync().then(async () => {
        setting.value = numMinutesUsed;
        try {
          await context.sync();
          LogEvents.MonthlyQuota.AddData([{ key: MonthlyQuotaKeys.AddMinutesUsedSucceeded, value: true }]);
        } catch (ex) {
          LogEvents.MonthlyQuota.AddData([{ key: MonthlyQuotaKeys.AddMinutesUsedSucceeded, value: false }]);
          LogError(LogEventNames.MonthlyQuota,"Using RichApi to add minutes got error:- " + ErrorStringFromException(ex));
        }
        try {
          await PromiseWithTimeout(this.SetUsedMinutesCacheAsync , MAX_MINUTES_GET_TIME_MILLISECONDS /*timeout*/, "SetUsedMinutesCacheAsync" /*promise name*/);
        } catch (error) {
          LogError(LogEventNames.MonthlyQuota, ErrorStringFromException(error));
        }
        LogEvents.MonthlyQuota.End([
          {key: MonthlyQuotaKeys.MinutesUsedAdded, value: this.usedMinutesCache},
        ]);
      });
    });
  }

  public async GetMonthlyMinutesUsed(): Promise<number> {
    let minutesUsed = 0;
    try {
      if (IS_DEV_BUILD) {
        return 42;
      }

      const roamingSetting = await this.GetMonthlyMinutesUsedRoamingSetting();
      if (roamingSetting !== undefined && roamingSetting.value !== undefined) {
        minutesUsed = roamingSetting.value;
      } else {
        LogError(
          LogEventNames.MonthlyQuota,
          `Received incorrect monthly minutes roaming setting ${roamingSetting}`,
        );
      }

      return minutesUsed;
    } catch (e) {
      LogError(LogEventNames.MonthlyQuota, ErrorStringFromException(e));
      throw e;
    }
  }

  private GetMonthlyMinutesUsedRoamingSetting(): Promise<any> {
    try {
      return OfficeCore.run(async (context) => {
        const setting = context.firstParty.roamingSettings
          .getItem(ROAMING_SETTING_MONTHLY_QUOTA_KEY)
          .load('value');
        await context.sync();
        return setting;
      });
    } catch (e) {
      LogError(LogEventNames.MonthlyQuota, ErrorStringFromException(e));
      throw e;
    }
  }

  public async SetUsedMinutesCacheAsync() : Promise<any> {
    try {
      TranscriptionMonthlyQuota.GetInstance().usedMinutesCache = await TranscriptionMonthlyQuota.GetInstance().GetMonthlyMinutesUsed();
    } catch (e) {
      throw e;
    }
  }
}